import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import type {
  DtoV1Borrower1003BorrowerSummaryAddress,
  DtoV1Borrower1003BorrowerSummaryData,
  DtoV1Borrower1003ResidenceHistoryDetails,
} from '~/__generated__/api';
import { useSnackbar } from '~/common/components/Snackbar/snackbarStore';
import useUrl from '~/common/hooks/useUrl';
import { setLoanSummaryUpsertOrDelete } from '~/common/redux/actions';
import { typedApiClient } from '~/common/util/api-client';

import { useBorrower1003Store } from '../../../borrower1003Store';
import { borrower1003QueryKeys, use1003BorrowerData } from '../../../hooks';
import type { AddressInfoData } from '../../../types';
import {
  dedupeAssociatedAddressesAgainstPrimary,
  getValidAddressId,
  mergePrimaryResidenceIntoHistory,
} from './primaryAddressSelection';

const UWAI_ADDRESS_FIELDS = [
  'addressLine1',
  'unit',
  'city',
  'state',
  'zipCode',
] as const;

type AddressInfoBorrowerData = Omit<
  DtoV1Borrower1003BorrowerSummaryData,
  'residenceHistoryDetails'
> & {
  residenceHistoryDetails?:
    | (DtoV1Borrower1003ResidenceHistoryDetails & {
        primaryResidence?: DtoV1Borrower1003BorrowerSummaryAddress | null;
        primaryResidenceSelectionId?: string;
      })
    | null;
};

function applyPrimaryAddressSelectionSplit(
  borrower: DtoV1Borrower1003BorrowerSummaryData | undefined,
  isPrimaryAddressSelectionEnabled: boolean,
): AddressInfoBorrowerData | undefined {
  if (!borrower) {
    return borrower;
  }

  const borrowerResidenceHistory =
    borrower.residenceHistoryDetails?.borrowerResidenceHistory ?? [];

  if (!isPrimaryAddressSelectionEnabled) {
    return {
      ...borrower,
      residenceHistoryDetails: {
        ...borrower.residenceHistoryDetails,
        primaryResidence: null,
        primaryResidenceSelectionId: '',
      },
    };
  }

  const primaryResidence =
    borrowerResidenceHistory.find(
      (history) => history.borrowerAddress?.primaryResidence,
    ) ?? null;

  const remainingResidenceHistory = borrowerResidenceHistory.filter(
    (history) => !history.borrowerAddress?.primaryResidence,
  );

  return {
    ...borrower,
    residenceHistoryDetails: {
      ...borrower.residenceHistoryDetails,
      primaryResidence,
      primaryResidenceSelectionId: String(
        primaryResidence?.addressIdentifier?.addressId ?? '',
      ),
      borrowerResidenceHistory: remainingResidenceHistory,
    },
  };
}

export function useAddressInfoFormData(borrowerId: number) {
  return use1003BorrowerData({
    select: useCallback(
      (s) => {
        const borrower = s.borrowers.find(
          (b) => b.customerRecordId === borrowerId,
        );

        return {
          formData: applyPrimaryAddressSelectionSplit(
            borrower,
            Boolean(s.isPrimaryAddressSelectionEnabled),
          ),
          initialPrimaryAddressId: s.isPrimaryAddressSelectionEnabled
            ? getValidAddressId(
                borrower?.residenceHistoryDetails?.borrowerResidenceHistory?.find(
                  ({ borrowerAddress }) => borrowerAddress?.primaryResidence,
                ),
              )
            : null,
          dropdownOptions: s.dropdownOptions,
          isPrimaryAddressSelectionEnabled: s.isPrimaryAddressSelectionEnabled,
          primaryAddressSelectionOptions:
            borrower?.primaryAddressSelectionOptions ?? [],
        };
      },
      [borrowerId],
    ),
  });
}

function normalizeResidenceHistoryAddress(
  history: DtoV1Borrower1003BorrowerSummaryAddress,
): DtoV1Borrower1003BorrowerSummaryAddress {
  const addressId = history.addressIdentifier?.addressId;

  return {
    ...history,
    addressIdentifier:
      typeof addressId === 'number'
        ? history.addressIdentifier
        : {
            addressId: 0,
            addressLdId: 0,
          },
    borrowerAddress: {
      ...history.borrowerAddress,
      addressType: history.borrowerAddress?.addressType ?? '',
    },
  };
}

function shouldTriggerAddressInfoUwaiMatching(
  initialHistory: DtoV1Borrower1003BorrowerSummaryAddress[] = [],
  currentHistory: DtoV1Borrower1003BorrowerSummaryAddress[] = [],
): boolean {
  if (currentHistory.length !== initialHistory.length) {
    return true;
  }

  const initialHistoryByAddressId = new Map<
    number,
    DtoV1Borrower1003BorrowerSummaryAddress
  >();

  initialHistory.forEach((history) => {
    const addressId = history.addressIdentifier?.addressId;

    if (typeof addressId === 'number') {
      initialHistoryByAddressId.set(addressId, history);
    }
  });

  return currentHistory.some((history, index) => {
    const addressId = history.addressIdentifier?.addressId;

    const matchingInitialHistory =
      typeof addressId === 'number'
        ? (initialHistoryByAddressId.get(addressId) ?? initialHistory[index])
        : initialHistory[index];

    if (!matchingInitialHistory) {
      return true;
    }

    const currentBorrowerAddress = history.borrowerAddress ?? {};
    const matchingInitialBorrowerAddress =
      matchingInitialHistory.borrowerAddress ?? {};

    const isAddressChanged = UWAI_ADDRESS_FIELDS.some((field) => {
      return (
        currentBorrowerAddress[field] !== matchingInitialBorrowerAddress[field]
      );
    });

    return (
      isAddressChanged || history.country !== matchingInitialHistory.country
    );
  });
}

type AddressInfoFormValues = AddressInfoData['formData'];
type AddressInfoResidenceHistoryDetails = NonNullable<
  AddressInfoFormValues['residenceHistoryDetails']
>;

type ResidenceHistoryUpdate = {
  residenceHistoryDetails: Omit<
    AddressInfoResidenceHistoryDetails,
    'primaryResidence' | 'primaryResidenceSelectionId'
  >;
  shouldTriggerUwaiEntityMatching: boolean;
};

function buildLegacyResidenceHistoryUpdate(
  values: AddressInfoFormValues,
  initialResidenceHistory: DtoV1Borrower1003BorrowerSummaryAddress[],
): ResidenceHistoryUpdate {
  const {
    primaryResidence: _primaryResidence,
    primaryResidenceSelectionId: _primaryResidenceSelectionId,
    ...legacyResidenceHistoryDetails
  } = values.residenceHistoryDetails;

  return {
    residenceHistoryDetails: {
      ...legacyResidenceHistoryDetails,
      borrowerResidenceHistory:
        legacyResidenceHistoryDetails.borrowerResidenceHistory?.map(
          normalizeResidenceHistoryAddress,
        ),
    },
    shouldTriggerUwaiEntityMatching: shouldTriggerAddressInfoUwaiMatching(
      initialResidenceHistory,
      values.residenceHistoryDetails.borrowerResidenceHistory ?? [],
    ),
  };
}

function buildPrimarySelectionResidenceHistoryUpdate(
  values: AddressInfoFormValues,
  initialResidenceHistoryDetails: AddressInfoResidenceHistoryDetails | null,
): ResidenceHistoryUpdate {
  const {
    primaryResidence,
    primaryResidenceSelectionId: _primaryResidenceSelectionId,
    ...residenceHistoryDetailsRest
  } = values.residenceHistoryDetails;

  const currentResidenceHistory = mergePrimaryResidenceIntoHistory(
    values.residenceHistoryDetails,
  );
  const initialResidenceHistory = mergePrimaryResidenceIntoHistory(
    initialResidenceHistoryDetails,
  );

  return {
    residenceHistoryDetails: {
      ...residenceHistoryDetailsRest,
      borrowerAssociatedAddresses: dedupeAssociatedAddressesAgainstPrimary(
        residenceHistoryDetailsRest.borrowerAssociatedAddresses,
        primaryResidence,
      ),
      borrowerResidenceHistory: currentResidenceHistory.map(
        normalizeResidenceHistoryAddress,
      ),
      previousResidences: [],
      currentResidences: [],
    },
    shouldTriggerUwaiEntityMatching: shouldTriggerAddressInfoUwaiMatching(
      initialResidenceHistory,
      currentResidenceHistory,
    ),
  };
}

export function useAddressInfoMutation(borrowerId: number) {
  const { obfuscatedLoanIdentifier } = useUrl();
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const { data } = useAddressInfoFormData(borrowerId);
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async (values: AddressInfoData['formData']) => {
      const isPrimaryAddressSelectionEnabled = Boolean(
        data?.isPrimaryAddressSelectionEnabled,
      );

      const residenceHistoryUpdate = isPrimaryAddressSelectionEnabled
        ? buildPrimarySelectionResidenceHistoryUpdate(
            values,
            data?.formData?.residenceHistoryDetails ?? null,
          )
        : buildLegacyResidenceHistoryUpdate(
            values,
            data?.formData?.residenceHistoryDetails?.borrowerResidenceHistory ??
              [],
          );

      await typedApiClient.v1.borrower1003ValidationUpdateBorrowerResidenceHistoryDetails(
        {
          obfuscatedLoanIdentifier,
          borrowerIdentifier: {
            borrowerLenderDatabaseId: values.lenderdatabaseId,
            borrowerRecordId: values.customerRecordId,
          },
          ...residenceHistoryUpdate,
        },
      );
    },
    onSuccess: () => {
      useBorrower1003Store.setState({ selectedBorrowerId: null });

      queryClient.invalidateQueries({
        queryKey: borrower1003QueryKeys.borrowerData(obfuscatedLoanIdentifier),
      });

      dispatch(setLoanSummaryUpsertOrDelete(true));

      showSnackbar({
        message: 'Address info updated successfully.',
        severity: 'success',
      });
    },
    onError: () => {
      showSnackbar({
        message: 'Failed to update address info.',
        severity: 'error',
      });
    },
  });
}
